import React from 'react';
import { motion } from 'framer-motion';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface AppearanceStepProps {
  hairColor: string;
  skinColor: string;
  onHairColorChange: (color: string) => void;
  onSkinColorChange: (color: string) => void;
  errors?: {
    hairColor?: string;
    skinColor?: string;
  };
}

// Define hair colors with larger swatches, no text
const hairColors = [
  { value: 'black', color: '#000000' },
  { value: 'brown', color: '#8B4513' },
  { value: 'blonde', color: '#F5DEB3' },
  { value: 'red', color: '#B22222' },
  { value: 'ginger', color: '#D2691E' },
  { value: 'auburn', color: '#A52A2A' },
  { value: 'dark brown', color: '#4A3728' },
  { value: 'light brown', color: '#B5651D' },
  { value: 'strawberry blonde', color: '#E5C8A8' },
  { value: 'platinum blonde', color: '#E6E6FA' },
  { value: 'grey', color: '#808080' },
  { value: 'white', color: '#FFFFFF' }
];

// Define skin tones with larger swatches, no text
const skinTones = [
  { value: 'fair', color: '#FFE4C4' },
  { value: 'light', color: '#F5DCBE' },
  { value: 'medium', color: '#E0C49E' },
  { value: 'olive', color: '#C19A6B' },
  { value: 'tan', color: '#D2B48C' },
  { value: 'dark', color: '#8D5524' },
  { value: 'deep', color: '#5D4037' }
];

export function AppearanceStep({
  hairColor,
  skinColor,
  onHairColorChange,
  onSkinColorChange,
  errors = {}
}: AppearanceStepProps) {
  const renderColorSwatch = (
    color: { value: string; color: string },
    isSelected: boolean,
    onClick: () => void,
    ariaLabel: string
  ) => (
    <motion.button
      key={color.value}
      type="button"
      onClick={onClick}
      className={cn(
        'relative w-full aspect-square rounded-lg transition-all',
        'focus:outline-none focus:ring-4 focus:ring-offset-2',
        isSelected ? [
          'ring-4 ring-purple-600 ring-offset-2 shadow-lg scale-110',
          'focus:ring-purple-500'
        ] : [
          'hover:scale-105 shadow-md',
          'focus:ring-gray-400'
        ]
      )}
      style={{ backgroundColor: color.color }}
      whileHover={{ scale: isSelected ? 1.1 : 1.08 }}
      whileTap={{ scale: 0.95 }}
      aria-label={ariaLabel}
      aria-selected={isSelected}
      role="radio"
    >
      {/* Border for white/very light colors */}
      {['#FFFFFF', '#E6E6FA', '#FFE4C4', '#F5DCBE'].includes(color.color) && (
        <div className="absolute inset-0 rounded-lg border border-gray-200" />
      )}
      
      {/* Selected checkmark */}
      {isSelected && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <div className="bg-white rounded-full p-1 shadow-lg">
            <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        </motion.div>
      )}
    </motion.button>
  );
  
  return (
    <div className="space-y-8">
      {/* Hair Color Selection */}
      <div className="space-y-4">
        <Label className="text-base font-medium">Hair Color</Label>
        <div className="grid grid-cols-4 md:grid-cols-6 gap-4">
          {hairColors.map(color => renderColorSwatch(
            color,
            hairColor === color.value,
            () => onHairColorChange(color.value),
            `${color.value} hair color`
          ))}
        </div>
        {errors.hairColor && (
          <p className="text-sm text-red-500">{errors.hairColor}</p>
        )}
      </div>
      
      {/* Skin Tone Selection */}
      <div className="space-y-4">
        <Label className="text-base font-medium">Skin Tone</Label>
        <div className="grid grid-cols-4 md:grid-cols-7 gap-4">
          {skinTones.map(tone => renderColorSwatch(
            tone,
            skinColor === tone.value,
            () => onSkinColorChange(tone.value),
            `${tone.value} skin tone`
          ))}
        </div>
        {errors.skinColor && (
          <p className="text-sm text-red-500">{errors.skinColor}</p>
        )}
      </div>
    </div>
  );
}
